import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useLazyQuery } from '@apollo/client';
import { GET_BOOKING_DETAILS } from '../../services/bookingService/queries';
import { GET_USER } from '../../services/authService/queries';
import { GET_INVOICE_BY_ID } from '../../services/invoiceServices/queries';
import { GET_DAMAGE_CLAIM } from '../../services/damageClaimService/queries';
import { isValidShortId, formatShortId } from '../../utils/short-id';
import { Button } from '../../components/button';
import { Marginer } from '../../components/marginer';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Colors } from '../../styles/colors';
import { PulseLoader } from 'react-spinners';

const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 max-w-screen-lg flex flex-col items-center px-4 py-8 mx-auto box-border`}
`;

const Title = styled.h1`
  ${tw`text-3xl font-bold text-text-primary mb-6`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-8 text-center max-w-2xl`}
`;

const SearchContainer = styled.div`
  ${tw`w-full bg-white shadow-card rounded-lg p-6 mb-8`}
`;

const InputGroup = styled.div`
  ${tw`flex flex-col md:flex-row gap-4 w-full`}
`;

const Input = styled.input`
  ${tw`flex-1 border border-border-default rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black`}
`;

const ResultContainer = styled.div`
  ${tw`w-full bg-white shadow-card rounded-lg p-6`}
`;

const ResultItem = styled.div`
  ${tw`border-b last:border-0 py-4`}
`;

const ResultLabel = styled.span`
  ${tw`font-semibold text-text-secondary mr-2`}
`;

const ResultValue = styled.span`
  ${tw`text-text-primary break-all`}
`;

const ErrorMessage = styled.div`
  ${tw`text-red-500 mt-2`}
`;

const EntityType = styled.div`
  ${tw`inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-md mb-2`}
`;

const ResultsGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-6 w-full mt-4`}
`;

const CategoryCard = styled.div<{ status: 'idle' | 'loading' | 'found' | 'not_found' }>`
  ${tw`bg-white shadow-card rounded-lg p-5 border-t-4 transition-all duration-300 w-full max-w-full min-w-0 box-border`}
  ${({ status }) => {
    switch (status) {
      case 'loading':
        return tw`border-blue-400 opacity-90`;
      case 'found':
        return tw`border-black`;
      case 'not_found':
        return tw`border-border-default opacity-70`;
      default:
        return tw`border-transparent`;
    }
  }}
`;

const CardTitle = styled.h3`
  ${tw`text-lg font-bold text-text-primary mb-3 flex justify-between items-center`}
`;

const StatusText = styled.span<{ type: 'found' | 'not_found' | 'loading' | 'idle' }>`
  ${tw`text-xs font-medium px-2 py-0.5 rounded-full`}
  ${({ type }) => {
    switch (type) {
      case 'found':
        return tw`bg-green-100 text-green-700`;
      case 'not_found':
        return tw`bg-red-100 text-red-700`;
      case 'loading':
        return tw`bg-blue-100 text-black`;
      default:
        return tw`bg-bg-secondary text-text-secondary`;
    }
  }}
`;

export default function ShortIdManagerPage() {
  const [shortId, setShortId] = useState('');
  const [error, setError] = useState('');

  const [getBooking, { loading: bookingLoading, data: bookingData, called: bookingCalled }] =
    useLazyQuery(GET_BOOKING_DETAILS, {
      onError: () => {},
    });
  const [getUser, { loading: userLoading, data: userData, called: userCalled }] = useLazyQuery(
    GET_USER,
    {
      onError: () => {},
    },
  );
  const [getInvoice, { loading: invoiceLoading, data: invoiceData, called: invoiceCalled }] =
    useLazyQuery(GET_INVOICE_BY_ID, {
      onError: () => {},
    });
  const [getDamageClaim, { loading: claimLoading, data: claimData, called: claimCalled }] =
    useLazyQuery(GET_DAMAGE_CLAIM, {
      onError: () => {},
    });

  const isLoading = bookingLoading || userLoading || invoiceLoading || claimLoading;

  const handleSearch = async () => {
    setError('');
    const trimmedId = shortId.trim().toLowerCase();

    if (!trimmedId) {
      setError('Please enter an ID');
      return;
    }

    if (!isValidShortId(trimmedId)) {
      setError('Invalid format. Short ID must be 8 characters.');
      return;
    }

    // Attempt to find in all categories
    getBooking({ variables: { id: trimmedId } });
    getUser({ variables: { id: trimmedId } });
    getInvoice({ variables: { id: trimmedId } });
    getDamageClaim({ variables: { id: trimmedId } });
  };

  const hasResults =
    bookingData?.booking || userData?.user || invoiceData?.invoice || claimData?.damageClaim;

  return (
    <PageContainer>
      <Title>Manage Short ID</Title>
      <Description>
        Search for a Booking, User, Invoice, or Damage Claim using its 8-character Virtual Short ID.
        This tool helps customer service agents quickly identify full UUIDs.
      </Description>

      <SearchContainer>
        <InputGroup>
          <Input
            placeholder="Enter 8-digit Short ID (e.g., 550e8400)"
            value={shortId}
            onChange={e => setShortId(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleSearch()}
          />
          <Button
            text={isLoading ? 'Searching...' : 'Search'}
            onClick={handleSearch}
            isDisabled={isLoading}
            buttonWidth="120px"
          />
        </InputGroup>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SearchContainer>

      <ResultsGrid>
        {/* Booking Section */}
        <CategoryCard
          status={
            !bookingCalled
              ? 'idle'
              : bookingLoading
                ? 'loading'
                : bookingData?.booking
                  ? 'found'
                  : 'not_found'
          }
        >
          <CardTitle>
            Booking
            {bookingCalled && (
              <StatusText
                type={bookingLoading ? 'loading' : bookingData?.booking ? 'found' : 'not_found'}
              >
                {bookingLoading ? 'Searching...' : bookingData?.booking ? 'Found' : 'Not Found'}
              </StatusText>
            )}
          </CardTitle>
          {bookingLoading && <PulseLoader size={8} color={Colors.PrimaryRed} />}
          {bookingData?.booking && (
            <div className="flex flex-col gap-2 mt-2">
              <div>
                <ResultLabel>Short ID:</ResultLabel>
                <ResultValue>{formatShortId(bookingData.booking.shortId)}</ResultValue>
              </div>
              <div>
                <ResultLabel>Full UUID:</ResultLabel>
                <ResultValue>{bookingData.booking.id}</ResultValue>
              </div>
              <div>
                <ResultLabel>Status:</ResultLabel>
                <ResultValue>{bookingData.booking.status}</ResultValue>
              </div>
              <div>
                <ResultLabel>Car:</ResultLabel>
                <ResultValue>
                  {bookingData.booking.car
                    ? `${bookingData.booking.car.year} ${bookingData.booking.car.make} ${bookingData.booking.car.model}`
                    : 'N/A (Broken link or deleted)'}
                </ResultValue>
              </div>
            </div>
          )}
          {!bookingLoading && bookingCalled && !bookingData?.booking && (
            <div tw="text-sm text-text-tertiary italic">No booking matching this ID</div>
          )}
        </CategoryCard>

        {/* User Section */}
        <CategoryCard
          status={
            !userCalled ? 'idle' : userLoading ? 'loading' : userData?.user ? 'found' : 'not_found'
          }
        >
          <CardTitle>
            User
            {userCalled && (
              <StatusText type={userLoading ? 'loading' : userData?.user ? 'found' : 'not_found'}>
                {userLoading ? 'Searching...' : userData?.user ? 'Found' : 'Not Found'}
              </StatusText>
            )}
          </CardTitle>
          {userLoading && <PulseLoader size={8} color={Colors.PrimaryRed} />}
          {userData?.user && (
            <div className="flex flex-col gap-2 mt-2">
              <div>
                <ResultLabel>Short ID:</ResultLabel>
                <ResultValue>{formatShortId(userData.user.shortId)}</ResultValue>
              </div>
              <div>
                <ResultLabel>Full UUID:</ResultLabel>
                <ResultValue>{userData.user.id}</ResultValue>
              </div>
              <div>
                <ResultLabel>Name:</ResultLabel>
                <ResultValue>{userData.user.displayName || 'N/A'}</ResultValue>
              </div>
              <div>
                <ResultLabel>Email:</ResultLabel>
                <ResultValue>{userData.user.email}</ResultValue>
              </div>
            </div>
          )}
          {!userLoading && userCalled && !userData?.user && (
            <div tw="text-sm text-text-tertiary italic">No user matching this ID</div>
          )}
        </CategoryCard>

        {/* Invoice Section */}
        <CategoryCard
          status={
            !invoiceCalled
              ? 'idle'
              : invoiceLoading
                ? 'loading'
                : invoiceData?.invoice
                  ? 'found'
                  : 'not_found'
          }
        >
          <CardTitle>
            Invoice
            {invoiceCalled && (
              <StatusText
                type={invoiceLoading ? 'loading' : invoiceData?.invoice ? 'found' : 'not_found'}
              >
                {invoiceLoading ? 'Searching...' : invoiceData?.invoice ? 'Found' : 'Not Found'}
              </StatusText>
            )}
          </CardTitle>
          {invoiceLoading && <PulseLoader size={8} color={Colors.PrimaryRed} />}
          {invoiceData?.invoice && (
            <div className="flex flex-col gap-2 mt-2">
              <div>
                <ResultLabel>Short ID:</ResultLabel>
                <ResultValue>{formatShortId(invoiceData.invoice.shortId)}</ResultValue>
              </div>
              <div>
                <ResultLabel>Full UUID:</ResultLabel>
                <ResultValue>{invoiceData.invoice.id}</ResultValue>
              </div>
              <div>
                <ResultLabel>Amount:</ResultLabel>
                <ResultValue>${invoiceData.invoice.amount}</ResultValue>
              </div>
              <div>
                <ResultLabel>Type:</ResultLabel>
                <ResultValue>{invoiceData.invoice.type}</ResultValue>
              </div>
            </div>
          )}
          {!invoiceLoading && invoiceCalled && !invoiceData?.invoice && (
            <div tw="text-sm text-text-tertiary italic">No invoice matching this ID</div>
          )}
        </CategoryCard>

        {/* Damage Claim Section */}
        <CategoryCard
          status={
            !claimCalled
              ? 'idle'
              : claimLoading
                ? 'loading'
                : claimData?.damageClaim
                  ? 'found'
                  : 'not_found'
          }
        >
          <CardTitle>
            Damage Claim
            {claimCalled && (
              <StatusText
                type={claimLoading ? 'loading' : claimData?.damageClaim ? 'found' : 'not_found'}
              >
                {claimLoading ? 'Searching...' : claimData?.damageClaim ? 'Found' : 'Not Found'}
              </StatusText>
            )}
          </CardTitle>
          {claimLoading && <PulseLoader size={8} color={Colors.PrimaryRed} />}
          {claimData?.damageClaim && (
            <div className="flex flex-col gap-2 mt-2">
              <div>
                <ResultLabel>Short ID:</ResultLabel>
                <ResultValue>{formatShortId(claimData.damageClaim.shortId)}</ResultValue>
              </div>
              <div>
                <ResultLabel>Full UUID:</ResultLabel>
                <ResultValue>{claimData.damageClaim.id}</ResultValue>
              </div>
              <div>
                <ResultLabel>Claim #:</ResultLabel>
                <ResultValue>{claimData.damageClaim.claimNum}</ResultValue>
              </div>
              <div>
                <ResultLabel>Status:</ResultLabel>
                <ResultValue>{claimData.damageClaim.status}</ResultValue>
              </div>
            </div>
          )}
          {!claimLoading && claimCalled && !claimData?.damageClaim && (
            <div tw="text-sm text-text-tertiary italic">No claim matching this ID</div>
          )}
        </CategoryCard>
      </ResultsGrid>
    </PageContainer>
  );
}
